import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-tooltip';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { PartExclusionFilter, PartExclusionHeader } from '../../models/part-exclusion.model';
import { PartExclusionService } from '../../services/part-exclusion.service';
import { AuthPolicies } from '../../auth/auth-policies';
import { environment } from '../../../environments/environment';
import { PaginationComponent } from '../../shared-components/aems-pagination/aems-pagination.component';
import { utils } from '../../helper/utils';
import { DownloadFilterCriteria, DownloadRequest, toFilter } from '../../models/download-filter.model';
import { downloadBlob, generateDateFilename, handleDownloadError } from '../../helper/download-helpers';
import { ToastrService } from '../../services/toastr-service';
import { AemsMultilineTagInputComponent } from '../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';

@Component({
  selector: 'app-part-exclusion',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, PaginationComponent, AemsMultilineTagInputComponent],
  templateUrl: './part-exclusion.component.html',
  styleUrl: './part-exclusion.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PartExclusionComponent implements OnInit {
  readonly partNumberRegex: RegExp = /^[A-Za-z0-9\- /]+$/;
  readonly filters = this.formBuilder.nonNullable.group({
    searchText: '',
    repairModeList: this.formBuilder.nonNullable.control<string[]>([]),
    partCodeDescriptionList: this.formBuilder.nonNullable.control<string[]>([]),
  });
  data: PartExclusionHeader[] = [];
  modes: string[] = [];
  loading = false;
  filtersOpen = false;
  currentPage = 1;
  pageLimit = 20;
  lastPage = 1;
  totalRecords = 0;
  sort: string | undefined;
  isDownload = false;
  readonly canAdd = this.authPolicies.canAddPartExclusion && environment.Features.EnablePartExclusion;
  readonly DOWNLOAD_COLUMNS = ['repair_mode', 'part_number', 'part_description', 'exclu_part_number'];
  readonly columns: Array<TableColumn> = [
    { id: 'repairMode', label: 'Mode', width: '100px' },
    { id: 'partNumber', label: 'Part Number', width: '150px' },
    { id: 'description', label: 'Description', sortDisabled: true, width: '320px' },
    { id: 'exclusionPartNumber', label: 'Exclusive Part Number', sortDisabled: true, width: '210px' },
    { id: 'updatedBy', label: 'Updated By', sortDisabled: true, width: '160px' },
    { id: 'updatedAt', label: 'Last Updated', sortDisabled: true, width: '180px', dataType: { type: 'string', formatter: (value) => utils.dateFormatter.format(new Date(String(value))) } },
  ];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly service: PartExclusionService,
    private readonly router: Router,
    public readonly authPolicies: AuthPolicies,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.service.getAllModes().subscribe((modes) => (this.modes = modes));
    this.load();
  }

  load(): void {
    this.loading = true;
    const raw = this.filters.getRawValue();
    const filters: PartExclusionFilter = {
      searchText: raw.searchText || undefined,
      repairModeList: raw.repairModeList.length ? raw.repairModeList : undefined,
      partCodeDescriptionList: raw.partCodeDescriptionList.length ? raw.partCodeDescriptionList : undefined,
    };
    this.service.getList(this.currentPage, this.pageLimit, this.sort, filters).subscribe((response) => {
      this.data = response.body ?? [];
      const headers = response.headers ?? new HttpHeaders();
      this.currentPage = Number(headers.get('Current-Page') ?? this.currentPage);
      this.lastPage = Number(headers.get('Last-Page') ?? 1);
      this.totalRecords = Number(headers.get('Total-Item-Count') ?? this.data.length);
      this.loading = false;
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.filtersOpen = false;
    this.load();
  }

  clearFilters(): void {
    this.filters.reset({ searchText: '', repairModeList: [], partCodeDescriptionList: [] });
    this.applyFilters();
  }

  search(): void {
    this.currentPage = 1;
    this.load();
  }

  openDetail(row: PartExclusionHeader): void {
    this.router.navigate(['/master-data/part-exclusion', row.repairMode, row.partNumber]);
  }

  addNew(): void {
    this.router.navigate(['/master-data/part-exclusion/add-new']);
  }

  onSort(event: Event): void {
    const detail = (event as CustomEvent<{ id?: string; direction?: string }>).detail;
    if (!detail?.id || !detail.direction) return;
    this.sort = `${detail.id}:${detail.direction === 'ascending' ? 'asc' : 'desc'}`;
    this.currentPage = 1;
    this.load();
  }

  pageChanged(page: number): void {
    this.currentPage = page;
    this.load();
  }

  pageSizeChanged(limit: number): void {
    this.pageLimit = limit;
    this.currentPage = 1;
    this.load();
  }

  onDownload(): void {
    this.isDownload = true;
    const request: DownloadRequest = {
      columns: this.DOWNLOAD_COLUMNS,
      filters: this.buildDownloadFilters(),
    };
    this.service.downloadToExcel(request).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('part-code-exclusion'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const raw = this.filters.getRawValue();

    if (raw.repairModeList.length > 0) {
      filters.push(toFilter('repair_mode', raw.repairModeList));
    }

    if (raw.partCodeDescriptionList.length > 0) {
      filters.push(toFilter('part_number', raw.partCodeDescriptionList));
    }

    return filters;
  }
}
